import { IWallClock } from '../../utils/time/IWallClock';
import { IAuthTokenData } from './authTokenTypes';
import { DateTime } from 'luxon';
import { DefaultWallClock } from '../../utils/time/DefaultWallClock';
import { makeAutoObservable } from 'mobx';

type AuthTokenHelperConfig = {
  wallClock: IWallClock;
  timeBeforeExpiryToRefresh: number;
  maxFailedAttempts: number;
  maxFailedRequests: number;
  exponentialBackoffBase: number;
  exponentialBackoffMax: number;
};

/**
 * Contains basic state and utility that would be used by all token types
 */
export class AuthTokenHelper {
  private config: AuthTokenHelperConfig;
  constructor(
    config: Partial<AuthTokenHelperConfig>,
    initialToken?: IAuthTokenData,
  ) {
    this.config = {
      wallClock: new DefaultWallClock(),
      timeBeforeExpiryToRefresh: 120,
      maxFailedAttempts: 3,
      maxFailedRequests: 1,
      exponentialBackoffBase: 2,
      exponentialBackoffMax: 60,

      ...config,
    };
    if (initialToken) {
      this.recordNewToken(initialToken.expiryDate);
    }

    makeAutoObservable(this);
  }

  private _expiryDate: DateTime | null = null;

  private _failedAttempts = 0;

  private _failedRequests = 0;

  private _backoffExpiry: DateTime | null = null;

  get expiryDate(): DateTime | null {
    return this._expiryDate;
  }

  get failedAttempts(): number {
    return this._failedAttempts;
  }

  get failedRequests(): number {
    return this._failedRequests;
  }

  get backoffExpiry(): DateTime | null {
    return this._backoffExpiry;
  }

  recordNewToken(expiryDate: DateTime | null): void {
    if (expiryDate) {
      this._expiryDate = expiryDate;
    }

    this._failedAttempts = 0;
    this._failedRequests = 0;
    this._backoffExpiry = null;
  }

  recordFailedRequest() {
    this._failedRequests++;
  }

  recordFailedAttempt() {
    if (this._failedAttempts === 0) {
      this._failedAttempts++;
      return;
    }

    const uncappedBackoffTime =
      this.config.exponentialBackoffBase ** this._failedAttempts;

    const backoffTime = Math.min(
      uncappedBackoffTime,
      this.config.exponentialBackoffMax,
    );

    const now = this.config.wallClock.now();
    this._backoffExpiry = now.plus({ seconds: backoffTime });
    this._failedAttempts++;
  }

  hasRemainingAttempts(): boolean {
    return this._failedAttempts < this.config.maxFailedAttempts;
  }

  isBackedOff(): boolean {
    if (!this._backoffExpiry) {
      return false;
    }

    const now = this.config.wallClock.now();
    return now < this._backoffExpiry;
  }

  isExpired(): boolean {
    if (!this._expiryDate) {
      return false;
    }

    const now = this.config.wallClock.now();
    return now > this._expiryDate;
  }

  needsRefresh(): boolean {
    if (!this._expiryDate) {
      return false;
    }

    const now = this.config.wallClock.now();
    return (
      now.plus({ seconds: this.config.timeBeforeExpiryToRefresh }) >
      this._expiryDate
    );
  }

  isInvalidatedByFailures() {
    return this._failedRequests >= this.config.maxFailedRequests;
  }
}
